import { getAllItemStates, getLocalCounts, getVocabularyGroups } from './db'
import type { InterestValue, ItemState } from '../types/daily'
import type { ExportedItemState, LocalDataExport } from '../types/localExport'

const toExportedState = (state: ItemState): ExportedItemState => ({
  key: state.key,
  issueId: state.issueId,
  itemId: state.itemId,
  issueDate: state.issueDate,
  reference: state.reference,
  interest: state.interest ?? undefined,
  savedAt: state.savedAt,
  firstOpenedAt: state.firstOpenedAt,
  lastOpenedAt: state.lastOpenedAt,
  updatedAt: state.updatedAt,
})

export async function buildLocalDataExport(
  exportedAt = new Date().toISOString(),
  theme: LocalDataExport['preferences']['theme'] = 'system',
): Promise<LocalDataExport> {
  const [states, groups, counts] = await Promise.all([getAllItemStates(), getVocabularyGroups(), getLocalCounts()])
  const savedItems = states
    .filter((state) => state.saved)
    .sort((left, right) => (right.savedAt ?? right.updatedAt).localeCompare(left.savedAt ?? left.updatedAt))
    .map((state) => ({ ...toExportedState(state), snapshot: state.savedSnapshot }))
  const feedback = states
    .filter((state): state is ItemState & { interest: Exclude<InterestValue, null> } => Boolean(state.interest))
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
    .map((state) => ({ ...toExportedState(state), interest: state.interest }))
  const readingHistory = states
    .filter((state) => state.firstOpenedAt)
    .sort((left, right) => (right.lastOpenedAt ?? '').localeCompare(left.lastOpenedAt ?? ''))
    .map(toExportedState)

  return {
    exportVersion: '1.1.0',
    exportedAt,
    product: 'Daily Read',
    localOnly: true,
    preferences: { theme },
    summary: {
      savedItems: counts.savedItems,
      feedbackItems: counts.feedbackItems,
      readingRecords: counts.readingRecords,
      vocabularyTerms: counts.vocabulary,
      reviewedVocabularyTerms: counts.reviewedVocabulary,
      pendingVocabularyTerms: counts.pendingVocabulary,
    },
    savedItems,
    feedback,
    readingHistory,
    vocabulary: groups.map((group) => ({
      normalizedTerm: group.normalizedTerm,
      term: group.term,
      saved: group.saved,
      firstOpenedAt: group.firstOpenedAt,
      lastOpenedAt: group.lastOpenedAt,
      reviewCount: group.reviewCount,
      lastReviewedAt: group.lastReviewedAt,
      occurrences: group.occurrences.map((occurrence) => ({
        key: occurrence.key,
        issueId: occurrence.issueId,
        itemId: occurrence.itemId,
        term: occurrence.term,
        lemma: occurrence.lemma,
        aliases: occurrence.aliases,
        phonetic: occurrence.phonetic,
        partOfSpeech: occurrence.partOfSpeech,
        difficulty: occurrence.difficulty,
        meaningZh: occurrence.meaningZh,
        meaningInContextZh: occurrence.meaningInContextZh,
        collocation: occurrence.collocation,
        firstOpenedAt: occurrence.firstOpenedAt,
        lastOpenedAt: occurrence.lastOpenedAt,
        surfaceNormalized: occurrence.surfaceNormalized,
        origin: occurrence.origin,
        lookupStatus: occurrence.lookupStatus,
        contextExcerpt: occurrence.contextExcerpt,
        dictionaryVersion: occurrence.dictionaryVersion,
        itemReference: occurrence.itemReference,
      })),
    })),
  }
}

export async function buildFeedbackSummary() {
  const data = await buildLocalDataExport()
  const topicCounts = (interest: 'interested' | 'not_interested') => {
    const counts = new Map<string, number>()
    for (const item of data.feedback.filter((entry) => entry.interest === interest)) {
      for (const topic of item.reference?.topics ?? []) counts.set(topic, (counts.get(topic) ?? 0) + 1)
    }
    return [...counts.entries()].sort((left, right) => right[1] - left[1]).slice(0, 8)
  }
  const formatTopics = (entries: Array<[string, number]>) => entries.length
    ? entries.map(([topic, count]) => `${topic}（${count}）`).join('、')
    : '暂无'
  const recent = (interest: 'interested' | 'not_interested') => data.feedback
    .filter((entry) => entry.interest === interest)
    .slice(0, 5)
    .map((entry) => `- ${entry.reference?.title ?? `${entry.issueId}/${entry.itemId}`}｜${entry.reference?.source.name ?? '来源未知'}`)

  return [
    '# Daily Read 本地反馈摘要',
    `生成时间：${data.exportedAt}`,
    '说明：摘要来自当前浏览器的本地记录，不代表服务端已自动收到反馈。',
    '',
    `收藏：${data.summary.savedItems} 条；阅读记录：${data.summary.readingRecords} 条；唯一词语：${data.summary.vocabularyTerms} 个。`,
    `感兴趣主题：${formatTopics(topicCounts('interested'))}`,
    `不感兴趣主题：${formatTopics(topicCounts('not_interested'))}`,
    '',
    '最近“感兴趣”的代表条目：',
    ...(recent('interested').length ? recent('interested') : ['- 暂无']),
    '',
    '最近“不感兴趣”的代表条目：',
    ...(recent('not_interested').length ? recent('not_interested') : ['- 暂无']),
    '',
    `近期词汇：${data.vocabulary.slice(0, 12).map((entry) => entry.term).join('、') || '暂无'}`,
  ].join('\n')
}
